import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { uploadFiles, fetchFiles, deleteFile, buildFileScopeKey } from '@/store/fileSlice';
import { fileAPI } from '@/api/fileAPI';
import { Paperclip, Loader2, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { FileMeta } from '@/types/files';
import { useClipboardFilePaste } from '@/hooks/useClipboardFilePaste';
import { FileCard } from '../FileCard';
import { FilePreviewModal } from '../FilePreviewModal';

interface FileUploadProps {
  taskId: string;
  source?: string;
  commentId?: string;
  onFilesSelected?: (files: File[]) => void;
  selectedFiles?: File[];
  isViewerOnly?: boolean; 
}

export const FileUpload: React.FC<FileUploadProps> = ({
  taskId,
  source = 'attachment',
  commentId,
  onFilesSelected,
  selectedFiles,
  isViewerOnly,
}) => {
  const dispatch = useAppDispatch();
  const { files, loadingByKey } = useAppSelector((state) => state.files);
  const { user } = useAppSelector((state) => state.auth);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileToDelete, setFileToDelete] = useState<FileMeta | null>(null);
  const [previewFile, setPreviewFile] = useState<FileMeta | null>(null);

  const scopeKey = buildFileScopeKey({ taskId, source, commentId });
  const isLoading = Boolean(loadingByKey?.[scopeKey]);

  // ✅ Fetch on mount
  useEffect(() => {
    if (taskId) {
      dispatch(fetchFiles({ taskId, source, commentId }));
    }
  }, [taskId, source, commentId, dispatch]);

  const handleFiles = useCallback(
    async (incoming: File[]) => {
      if (!incoming.length) return;

      if (onFilesSelected) {
        onFilesSelected(incoming);
        return;
      }

      await dispatch(
        uploadFiles({
          files: incoming,
          taskId,
          source,
          commentId,
          uploadedBy: user?.id,
          uploadedUserEmail: user?.email,
        }),
      ).unwrap();
    },
    [commentId, dispatch, onFilesSelected, source, taskId, user?.email, user?.id],
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const selected = Array.from(files);

    try {
      await handleFiles(selected);
    } catch (error) {
      console.error('Failed to upload files', error);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = useClipboardFilePaste({
    contextLabel: source === 'comment' ? 'comment-attachment' : 'attachment',
    onFiles: async (files) => {
      try {
        await handleFiles(files);
      } catch (error) {
        console.error('Failed to upload pasted files', error);
      }
    },
  });

  const handleDownload = async (file: FileMeta) => {
    try {
      await fileAPI.downloadFile(file._id, file.file_name);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    try {
      await dispatch(deleteFile(fileToDelete._id)).unwrap();
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    } finally {
      setFileToDelete(null);
    }
  };


  // ✅ Filter for this section only
  const visibleFiles = files.filter(
    (f) =>
      f.task_id === taskId &&
      f.source === source &&
      (source !== 'comment' || f.comment_id === commentId)
  );

  return (
    <div
      className="relative bg-white border border-gray-200 rounded-lg p-4 outline-none focus:ring-2 focus:ring-blue-200"
      tabIndex={0}
      onPaste={handlePaste}
      role="group"
      aria-label="Attachment uploader"
    >
      {isLoading && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg z-10">
          <Loader2 className="animate-spin h-5 w-5 text-blue-600 mr-2" />
          <span className="text-sm text-gray-700">Processing...</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-gray-500" />
          {source === 'comment' ? 'Comment Attachments' : 'Attachments'}
        </h4>

        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
        />
        {!isViewerOnly && (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
        >
          <Paperclip className="h-3 w-3" /> Add
        </button>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-3">Tip: Focus this panel and press Ctrl+V to paste screenshots.</p>

      {/* Selected (not yet uploaded) */}
      {selectedFiles && selectedFiles.length > 0 && (
        <div className="mb-3 border-t border-gray-200 pt-2 space-y-1">
          {selectedFiles.map((file, i) => (
            <div key={i} className="text-sm text-gray-700 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" /> {file.name}
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files - Card Format */}
      {visibleFiles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {visibleFiles.map((file) => (
            <FileCard
              key={file._id}
              file={file}
              size="md"
              showDelete={true}
              onPreview={() => setPreviewFile(file)}
              onDownload={() => handleDownload(file)}
              onDelete={() => setFileToDelete(file)}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic text-center py-4">No attachments yet</p>
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />

      {/* Delete Confirmation */}
      {fileToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80 text-center">
            <h3 className="font-semibold text-gray-800 mb-2">Delete Attachment?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-800">{fileToDelete.file_name}</span>?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setFileToDelete(null)}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
